/**
 *
 * @description 兑换第三方优惠卡，需要记录用户邮箱、使用地区等信息
 * @time 2022/6/17 17:47
 *
 */

import React, { forwardRef, useRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useForm, FormProvider, useFormContext, useWatch, Controller, useController } from 'react-hook-form';
import { inject } from 'mobx-react';
import SubmitBtn from '@/components/SubmitBtn';
import MiddleModal from '@/components/MiddleModal';
import { message, Cascader, Select } from 'antd';
import { phoneRule, checkPhoneCode, EMAIL_PATTERN } from './common/utils';
import Api from './common/api';
import { getToken } from '@/utils';

import styles from './index.module.less';

require('./common/index.less');

const PROFILE_STORAGE_KEY = 'modal_physical_product.profile';

function FormItemInput({ label, name, rules, extraText }) {
  const [focus, setFocus] = useState(false);

  const {
    register,
    formState: { errors },
  } = useFormContext();
  const value = useWatch({ name });

  return (
    <div className={['form-item', focus ? 'form-item-focus' : ''].join(' ')}>
      <div className={['item-container', errors[name] ? 'item-container-error' : ''].join(' ')}>
        <div className={['item-wrap'].join(' ')}>
          <input
            className='custom-input'
            {...register(name, { setValueAs: (v) => v?.trim(), ...rules })}
            onFocus={() => setFocus(true)}
            onBlur={() => setFocus(false)}
          />
          <div className='bg-placeholder' />
        </div>
        <label
          className={[rules && rules.required ? 'required' : '', !!value || (focus && !value) ? 'focus' : ''].join(' ')}
        >
          {label}
        </label>
      </div>
      <div className='error'>{errors && errors[name]?.message}</div>
      {extraText && <div className='extra'>{extraText}</div>}
    </div>
  );
}

// 手机号码
function FormItemPhone({ phoneCodeList, names, labels, rules }) {
  const [focusPhoneCode, setFocusPhoneCode] = useState(false);
  const [focusPhoneNum, setFocusPhoneNum] = useState(false);

  const {
    control,
    register,
    formState: { errors },
  } = useFormContext();

  const phoneCodeValue = useWatch({ name: names[0] });
  const phoneNumValue = useWatch({ name: names[1] });

  const formItemRef = useRef();

  return (
    <div
      ref={formItemRef}
      className={['form-item', focusPhoneCode || focusPhoneNum ? 'form-item-focus' : ''].join(' ')}
    >
      <div className={['item-container', errors[names[0]] || errors[names[1]] ? 'item-container-error' : ''].join(' ')}>
        <div className='group-layout'>
          {/* 区号 */}
          <div className='item-block'>
            <div className={['item-wrap'].join(' ')}>
              <Controller
                name={names[0]}
                control={control}
                rules={rules[0]}
                render={({ field: { value, onChange } }) => (
                  <Select
                    getPopupContainer={() => formItemRef.current}
                    style={{ width: '100%' }}
                    showSearch
                    placeholder=''
                    dropdownMatchSelectWidth={false}
                    value={value}
                    onChange={(index) => {
                      onChange(index);
                    }}
                    optionLabelProp='label'
                    optionFilterProp='filterkeys'
                    onDropdownVisibleChange={(v) => setFocusPhoneCode(v)}
                    filterOption={(input, option) =>
                      option.props.filterkeys.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    {phoneCodeList?.map((el, idx) => (
                      <Select.Option
                        key={el.phoneCode + el.countryCode + idx}
                        value={idx}
                        filterkeys={el.countryCode + ' +' + el.phoneCode + el.areaFullName}
                        label={el.countryCode + ' +' + el.phoneCode}
                      >
                        <div className='phonecode-row'>
                          <span>{el.countryCode + ' +' + el.phoneCode}</span>
                          <span>{el.areaFullName}</span>
                        </div>
                      </Select.Option>
                    ))}
                  </Select>
                )}
              />
              <div className='bg-placeholder' />
            </div>
            <label
              className={[
                rules[0] && rules[0].required ? 'required' : '',
                typeof phoneCodeValue == 'number' || (focusPhoneCode && typeof phoneCodeValue !== 'number')
                  ? 'focus'
                  : '',
              ].join(' ')}
            >
              {labels[0]}
            </label>
          </div>

          {/* 手机号码 */}
          <div className='item-block'>
            <div className={['item-wrap'].join(' ')}>
              <input
                className='custom-input'
                {...register(names[1], rules[1])}
                onFocus={() => setFocusPhoneNum(true)}
                onBlur={() => setFocusPhoneNum(false)}
              />
              <div className='bg-placeholder' />
            </div>
            <label
              className={[
                rules[1] && rules[1].required ? 'required' : '',
                !!phoneNumValue || (focusPhoneNum && !phoneNumValue) ? 'focus' : '',
              ].join(' ')}
            >
              {labels[1]}
            </label>
          </div>
        </div>
      </div>
      <div className='error'>{errors && errors[names[0]]?.message}</div>
      <div className='error'>{errors && errors[names[1]]?.message}</div>
    </div>
  );
}

// 国家地区
function FormItemCountry({ countryList, name, label, rules, disabled }) {
  const [focus, setFocus] = useState(false);

  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext();
  const value = useWatch({ name });

  const labelFocus = useMemo(() => {
    // 国家地区 空数组时返回false
    if (!!value && Array.isArray(value) && value.length == 0 && !focus) {
      return false;
    }

    return !!value || (focus && !value);
  }, [value, focus]);

  return (
    <div className={['form-item', focus ? 'form-item-focus' : ''].join(' ')}>
      <div className={['item-container', errors[name] ? 'item-container-error' : ''].join(' ')}>
        <div className={['item-wrap'].join(' ')}>
          <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field: { value, onChange } }) => (
              <Cascader
                placeholder=''
                disabled={disabled}
                expandTrigger='hover'
                options={countryList}
                onDropdownVisibleChange={(visible) => {
                  setFocus(visible);
                }}
                displayRender={(label) => {
                  return label[label.length - 1];
                }}
                value={value}
                onChange={(e) => {
                  onChange(e);
                }}
              />
            )}
          ></Controller>
          <div className={['bg-placeholder', disabled ? 'bg-placeholder-disabled' : ''].join(' ')}></div>
        </div>
        <label
          className={[
            rules && rules.required ? 'required' : '',
            labelFocus ? 'focus' : '',
            disabled ? 'disabled' : '',
          ].join(' ')}
        >
          {label}
        </label>
      </div>
      <div className='error'>{errors && errors[name]?.message}</div>
    </div>
  );
}

function InputSpecification({ LANG, list, value, onChange }) {
  useEffect(() => {
    if (list.length > 0) {
      for (let item of list) {
        if (item.stock > 0) {
          onChange(item.id);
          break;
        }
      }
    }
  }, [list]);

  return (
    <div className='radio-group'>
      {(list || []).map((item) => {
        const noStock = item.stock <= 0;

        if (noStock) {
          return (
            <div className='radio-item no-stock' key={item.id}>
              {item.name}

              <div className='tag'>{LANG['product.modal_physical_product.out_of_stock']}</div>
            </div>
          );
        }

        return (
          <div
            className={['radio-item', item.id === value ? 'selected' : ''].join(' ')}
            key={item.id}
            onClick={() => {
              onChange(item.id);
            }}
          >
            {item.name}
          </div>
        );
      })}
    </div>
  );
}

const AddressForm = forwardRef((props, ref) => {
  const { LANG, isMobile } = props;

  const { labels, errorMessage } = useMemo(() => {
    const labels = {};
    const errorMessage = {};

    try {
      JSON.parse(LANG['product.modal_physical_product.address_form_labels']).forEach((e) => {
        labels[e.form_key] = e.tip;
      });
      JSON.parse(LANG['product.modal_physical_product.address_form_errors']).forEach((e) => {
        errorMessage[e.form_key] = e.tip;
      });

      return {
        labels,
        errorMessage,
      };
    } catch (e) {
      return {
        labels,
        errorMessage,
      };
    }
  }, [LANG]);

  const { specificationsTitle, specificationList } = props.specificationsDetail;
  const [specificationsId, setSpecificationsId] = useState();

  const methods = useForm({
    shouldFocusError: false,
    resetOptions: {
      keepDirtyValues: true,
    },
    mode: 'onSubmit',
  });

  const {
    handleSubmit,
    control,
    trigger,
    reset,
    formState: { isDirty },
  } = methods;

  // 根据国家地区判断表单字段
  const country = useWatch({ name: 'country', control });
  const phoneCodeIndex = useWatch({ name: 'phoneCode', control });

  const onSubmit = handleSubmit((data) => {
    // 格式化字段
    // countryCode 字段是上次的值，没有使用，最终值需要从 country 取出
    const { countryCode, phoneCode: phoneCodeIndex, country, ...values } = data;

    const countryList = props.countryList.reduce((prev, cur) => {
      prev = [...prev, ...cur.children];
      return prev;
    }, []);

    let countryName = country[country.length - 1];
    let targetCountry = countryList.find((el) => el.value == countryName);

    values.country = countryName;

    values.countryCode = targetCountry.countryCode;
    values.phoneCode = `+${props.phoneCodeList[phoneCodeIndex].phoneCode}`;

    values.specificationsId = specificationsId;

    props.onFinish(values);
  });

  useImperativeHandle(ref, () => {
    return {
      submit: onSubmit,
    };
  });

  const setFormDefaultValues = async (cache) => {
    let formData = {};

    if (props.data?.id) {
      // 处理国家地区格式 country
      formData.country = ((code) => {
        let ret = [];
        props.countryList.forEach((e) => {
          e.children.forEach((e2) => {
            if (e2.value === code) {
              ret[0] = e.value;
              ret[1] = e2.value;
            }
          });
        });
        return ret;
      })(props.data.country);

      // 区号处理(匹配区号)
      formData.phoneCode = ((code, countryCode) => {
        let check = checkPhoneCode(code, countryCode, props.phoneCodeList);

        return check.index != -1 ? check.index : null;
      })(props.data.phoneCode, props.data.phoneCodeArea || props.data.countryCode);

      formData = { ...props.data, ...formData };
    }

    // 工单创建时会传入禁用国家地区选择。
    // MF的情况也可以这里处理
    if (props.country && props.country.disabled) {
      let code = props.country.countryCode; // 传入的countryCode

      formData.country = ((code) => {
        let ret = [];
        props.countryList.forEach((e) => {
          e.children.forEach((e2) => {
            if (e2.countryCode === code) {
              ret[0] = e.value;
              ret[1] = e2.value;
            }
          });
        });
        return ret;
      })(code);

      // 其它国家 不填充
      if (props.country.countryCode !== 'MF' && !props.data?.id) {
        // 找到当前国家的phoneCode
        let phoneCodeItem = props.phoneCodeList.find((el) => el.countryCode == code);

        formData.phoneCode = ((code, countryCode) => {
          let check = checkPhoneCode(code, countryCode, props.phoneCodeList);

          return check.index != -1 ? check.index : null;
        })(phoneCodeItem.phoneCode, code);
      }
    }

    if (cache) {
      formData.email = cache.email;
      formData.address = cache.address;
    }

    reset(formData);
  };

  useEffect(() => {
    // 设置默认值
    let cache = {};

    if (localStorage[PROFILE_STORAGE_KEY]) {
      try {
        cache = JSON.parse(localStorage[PROFILE_STORAGE_KEY]);
      } catch (err) {
        console.log(err);
      }
    }

    setFormDefaultValues(cache);
  }, []);

  return (
    <FormProvider {...methods}>
      <div className={['form-layout', isMobile ? 'mobile' : ''].join(' ')}>
        <form onSubmit={onSubmit}>
          <div className='group-spec'>
            <div className='group-title'>{specificationsTitle?.titleName}</div>
            <div className='wrapper-input'>
              <InputSpecification
                LANG={LANG}
                list={specificationList || []}
                value={specificationsId}
                onChange={(id) => {
                  setSpecificationsId(id);
                }}
              />
            </div>
          </div>

          <div className='group-address'>
            <div className='group-title'>{LANG['product.modal_physical_product.address']}</div>

            <div className='form-row'>
              <FormItemInput
                label={labels['last_name']}
                name='lastName'
                rules={{
                  required: errorMessage['last_name'],
                  maxLength: {
                    value: 19,
                    message: LANG['product.modal_physical_product.max_char_check'].replace('$1', 19),
                  },
                }}
              />
              <FormItemInput
                label={labels['first_name']}
                name='firstName'
                rules={{
                  required: errorMessage['first_name'],
                  maxLength: {
                    value: 19,
                    message: LANG['product.modal_physical_product.max_char_check'].replace('$1', 19),
                  },
                }}
              />
            </div>

            <div className='form-row form-row-responsive'>
              {/* 区号代码返回列表的下标 */}
              <FormItemPhone
                phoneCodeList={props.phoneCodeList}
                names={['phoneCode', 'phone']}
                labels={[labels['phone_code'], labels['phone']]}
                rules={[
                  {
                    required: errorMessage['phone_code'],
                  },
                  {
                    required: errorMessage['phone'],
                    maxLength: {
                      value: 20,
                      message: LANG['product.modal_physical_product.max_char_check'].replace('$1', 20),
                    },
                    // 选择 +86 则校验11位手机号码
                    pattern: phoneRule(props.phoneCodeList?.[phoneCodeIndex]?.phoneCode == 86 ? '中国大陆' : ''),
                    // country?.[country.length - 1]
                  },
                ]}
              />

              <FormItemInput
                label={labels['email']}
                name='email'
                rules={{
                  required: errorMessage['email'],
                  pattern: {
                    value: EMAIL_PATTERN,
                    message: errorMessage['email'],
                  },
                }}
              />
            </div>

            <div className='form-row form-row-responsive'>
              <FormItemCountry
                name='country'
                label={labels['country']}
                rules={{ required: errorMessage['country'] }}
                countryList={props.countryList}
                disabled={props.country?.disabled}
              />

              <FormItemInput
                label={labels['zip_code']}
                name='zipCode'
                rules={{
                  maxLength: {
                    value: 40,
                    message: LANG['product.modal_physical_product.max_char_check'].replace('$1', 40),
                  },
                }}
              />
            </div>

            <div className='form-row form-row-responsive'>
              <FormItemInput
                label={labels['address']}
                name='address'
                rules={{
                  required: errorMessage['address'],
                  maxLength: {
                    value: 200,
                    message: LANG['product.modal_physical_product.max_char_check'].replace('$1', 200),
                  },
                }}
              />
            </div>

            <div className='form-tip'>{LANG['product.modal_physical_product.tip_address']}</div>
          </div>
        </form>
      </div>
    </FormProvider>
  );
});

const Index = inject(
  'area',
  'gold',
  'app'
)(
  forwardRef((props, ref) => {
    const refAddressForm = useRef();

    const {
      LANG,
      country: { code, language },
    } = props.area;
    const { isMobile } = props.app;

    const { productDetail, onExchangeProductSuccess } = props;
    const [visible, setVisible] = useState(false);
    const [countryList, setCountryList] = useState([]);
    const [phoneCodeList, setPhoneCodeList] = useState([]);
    const [specificationsDetail, setSpecificationsDetail] = useState({});

    const toggleVisible = (visible) => {
      setVisible(visible);
    };

    useImperativeHandle(ref, () => ({
      toggleVisible,
    }));

    React.useEffect(() => {
      const token = getToken();

      if (!token || productDetail.productType !== 'physical_product') {
        return;
      }

      Api.getSpecificationsDetail({
        CountryCode: code,
        X_Language: language,
        root: props.app,
        productId: productDetail.id,
      }).then((res) => {
        setSpecificationsDetail(res.data.specificationsDetail);
      });

      Api.getListCountry({ root: props.app }).then((res) => {
        if (res.code === 0) {
          let map = res.data.map;
          // 筛选去除 HongKong
          let hkIndex = map.AS?.findIndex((el) => el.name == 'Hong Kong');
          map.AS.splice(hkIndex, 1);

          const countryTitle = {};
          JSON.parse(LANG['common.data.countryTitleAddress']).forEach((e) => {
            countryTitle[e.c_key] = e.title;
          });

          const countryList = Object.keys(map).map((e) => {
            return {
              value: e,
              label: countryTitle[e] || e,
              children: (() => {
                let ret = map[e].map((e2) => {
                  return {
                    value: e2.name,
                    label: e2.name,
                    countryCode: e2.code,
                  };
                });
                return ret;
              })(),
            };
          });

          setCountryList(countryList);
        }
      });

      Api.getListPhoneCode({ root: props.app }).then((res) => {
        if (res.code == 0 && res.data.phoneCodes) {
          setPhoneCodeList(res.data.phoneCodes);
        }
      });
    }, []);

    return (
      <MiddleModal
        title={LANG['product.redemptionInfo']}
        footer={null}
        theme='float'
        visible={visible}
        wrapClassName={styles.submitModal}
        onCancel={() => toggleVisible(false)}
        width={640}
      >
        <AddressForm
          ref={refAddressForm}
          countryList={countryList}
          phoneCodeList={phoneCodeList}
          specificationsDetail={specificationsDetail}
          LANG={LANG}
          isMobile={isMobile}
          onFinish={(values) => {
            const postData = {
              productId: productDetail.id,
              specificationsId: values.specificationsId,
              name:
                language === 'zh_cn'
                  ? values.lastName + ' ' + values.firstName
                  : values.firstName + ' ' + values.lastName,
              phoneCode: values.phoneCode,
              phoneNumber: values.phone,
              country: values.countryCode,
              address: values.address,
              zip: values.zipCode,
              contactEmail: values.email,
            };

            Api.exchangePhysicalProduct({
              root: props.app,
              data: postData,
              X_Language: language,
              CountryCode: values.countryCode,
            }).then((res) => {
              if (res.code === 0) {
                toggleVisible(false);
                onExchangeProductSuccess?.();
              } else if (res.code === 5003) {
                const msg = LANG['product.redeemedMax']
                  .replace('%d', productDetail.period)
                  .replace('%t', productDetail.periodTime);
                message.warning(msg);
              } else if (res.code === 5004) {
                message.warning(LANG['product.exchangeBlackList']);
              } else if (res.code === 5005) {
                message.warning(LANG['product.notExist']);
              } else if (res.code === 16000) {
                message.warning(LANG['product.goldNotEnough']);
              } else if (res.code === 5030) {
                message.warning(LANG['product.modal_physical_product.area_not_support']);
              } else {
                message.warning(res.code);
              }
            });

            localStorage[PROFILE_STORAGE_KEY] = JSON.stringify({
              address: values.address,
              email: values.email,
            });
          }}
        />

        <div className={styles.modalFooter}>
          <div className={styles.point}>
            <div className={styles.pointText1}>{LANG['common.label.gold']}</div>
            <div className={styles.pointText2}>{productDetail.points}</div>
          </div>

          <div className={styles.btnGroup}>
            <SubmitBtn
              className={styles.submit}
              onClick={() => {
                refAddressForm.current.submit();
              }}
            >
              {LANG['common.btn.redeemNow']}
            </SubmitBtn>
          </div>
        </div>
      </MiddleModal>
    );
  })
);

export default Index;
